import React from 'react';
import SurveyTransferMappingItem from '../Domain/SurveyTransferMappingItem.js';
import SurveyTransferQuestionMappingItem from '../Domain/SurveyTransferQuestionMappingItem.js';
import SurveyTransferAnswerOptionMappingItem from '../Domain/SurveyTransferAnswerOptionMappingItem';
import AuthService from './authService.js';
import SearchParams from '../Elements/SearchParams';
import SurveyTransferMappingSearchParams from '../Contents/RoverImport/SurveyTransferMappingAdmin/SurveyTransferMappingSearchParams';
import SurveyTransferQuestionRulesSearchParams from '../Contents/RoverImport/SurveyTransferMappingAdmin/SurveyTransferQuestionRulesSearchParams';
import SurveyTransferAnswerOptionRulesSearchParams from '../Contents/RoverImport/SurveyTransferMappingAdmin/SurveyTransferAnswerOptionRulesSearchParams';

class SurveyTransferMappingService extends React.Component {
  // Signature matches what TabOfDataTable calls (orderById, orderDir, pageNumber, numberOfElements,
  // searchItem, clearCache). The backend endpoint returns the full active list, so the paging/search
  // arguments are accepted but not used.
  static GetSurveyTransferMappings() {
    return AuthService.getInstance().CallBackendFunction(
      '/api/SurveyTransferMapping/GetSurveyTransferMappings?clearCache=false'
    );
  }

  // Server-side searchable + paginated list. Signature matches what TabOfDataTable calls
  // (orderById, orderDir, pageNumber, numberOfElements, searchItem, clearCache). Returns a
  // PaginatedResponse: { Data, PageNumber, NumberOfElementsInTheTable, NumberOfElementsOnAPage }.
  static GetListBySearching(orderById, orderDir, pageNumber, numberOfElements, searchItem, clearCache = false) {
    const searchParams = new SearchParams(
      searchItem,
      SurveyTransferMappingSearchParams,
      orderById,
      orderDir,
      pageNumber,
      numberOfElements
    );
    return AuthService.getInstance().CallBackendFunction(
      `/api/SurveyTransferMapping/GetSurveyTransferMappingsBySearching?searchParams=${searchParams.serializeForBackendCall()}&clearCache=${clearCache}`
    );
  }

  static ConvertDBItems(itemlist) {
    let mappedItems = [];

    if (itemlist !== null) {
      mappedItems = itemlist.map((item) => new SurveyTransferMappingItem(item));
    }

    return mappedItems;
  }

  // Fetches a single survey transfer mapping (by id) via the searching endpoint, resolving to the
  // mapping resource (with source/target survey names) or null. Used by the question-rules header.
  static GetSurveyTransferMappingById(surveyTransferMappingId) {
    const searchParams = {
      Command: '',
      OrderBy: 'ClientName',
      OrderDirection: 'Asc',
      PageNumber: 1,
      NumberOfElementsOnAPage: 1,
      SearchItem: { SurveyTransferMappingId: surveyTransferMappingId },
    };
    return AuthService.getInstance()
      .CallBackendFunction(
        `/api/SurveyTransferMapping/GetSurveyTransferMappingsBySearching?searchParams=${JSON.stringify(
          searchParams
        )}&clearCache=false`
      )
      .then((result) => (result && result.data && result.data.length > 0 ? result.data[0] : null));
  }

  // Paginated + filterable question rules (question mappings) of a single survey transfer
  // mapping. Signature matches what TabOfDataTable calls. The parent mapping scope travels
  // in searchItem.SurveyTransferMappingId.
  static GetQuestionRulesBySearching(
    orderById,
    orderDir,
    pageNumber,
    numberOfElements,
    searchItem,
    clearCache = false
  ) {
    const searchParams = new SearchParams(
      searchItem,
      SurveyTransferQuestionRulesSearchParams,
      orderById,
      orderDir,
      pageNumber,
      numberOfElements
    );
    return AuthService.getInstance().CallBackendFunction(
      `/api/SurveyTransferMapping/GetSurveyTransferQuestionRules?searchParams=${searchParams.serializeForBackendCall()}&clearCache=${clearCache}`
    );
  }

  // Fetches a single question mapping (by its own id) via the question-rules search endpoint,
  // resolving to the question-mapping resource (texts + third-party question ids + parent
  // SurveyTransferMappingId) or null. Used by the answer-option page header.
  static GetQuestionMappingById(surveyTransferQuestionMappingId) {
    const searchParams = {
      Command: '',
      OrderBy: 'TargetTpQuestionDisplayOrder',
      OrderDirection: 'Asc',
      PageNumber: 1,
      NumberOfElementsOnAPage: 1,
      SearchItem: { SurveyTransferQuestionMappingId: surveyTransferQuestionMappingId },
    };
    return AuthService.getInstance()
      .CallBackendFunction(
        `/api/SurveyTransferMapping/GetSurveyTransferQuestionRules?searchParams=${JSON.stringify(
          searchParams
        )}&clearCache=false`
      )
      .then((result) => (result && result.data && result.data.length > 0 ? result.data[0] : null));
  }

  static ConvertQuestionRuleDBItems(itemlist) {
    let mappedItems = [];

    if (itemlist !== null) {
      mappedItems = itemlist.map((item) => new SurveyTransferQuestionMappingItem(item));
    }

    return mappedItems;
  }

  // Paginated + filterable answer-option rules (answer option mappings) of a single question
  // rule. Signature matches what TabOfDataTable calls. The parent question-mapping scope
  // travels in searchItem.SurveyTransferQuestionMappingId.
  static GetAnswerOptionRulesBySearching(
    orderById,
    orderDir,
    pageNumber,
    numberOfElements,
    searchItem,
    clearCache = false
  ) {
    const searchParams = new SearchParams(
      searchItem,
      SurveyTransferAnswerOptionRulesSearchParams,
      orderById,
      orderDir,
      pageNumber,
      numberOfElements
    );
    return AuthService.getInstance().CallBackendFunction(
      `/api/SurveyTransferMapping/GetSurveyTransferAnswerOptionRules?searchParams=${searchParams.serializeForBackendCall()}&clearCache=${clearCache}`
    );
  }

  static ConvertAnswerOptionRuleDBItems(itemlist) {
    let mappedItems = [];

    if (itemlist !== null) {
      mappedItems = itemlist.map((item) => new SurveyTransferAnswerOptionMappingItem(item));
    }

    return mappedItems;
  }

  // Creates a new mapping from the wizard's edited item. Ids are coerced to numbers
  // (select values arrive as strings) so the backend int? binding succeeds.
  static CreateSurveyTransferMapping(editedItem) {
    const toIntOrNull = (value) => (value === '' || value === null || value === undefined ? null : parseInt(value, 10));

    const payload = {
      ClientId: toIntOrNull(editedItem.ClientId),
      SourceSystemTpInstanceId: toIntOrNull(editedItem.SourceSystemTpInstanceId),
      SourceTpSurveyId: toIntOrNull(editedItem.SourceTpSurveyId),
      TargetSystemTpInstanceId: toIntOrNull(editedItem.TargetSystemTpInstanceId),
      TargetTpSurveyId: toIntOrNull(editedItem.TargetTpSurveyId),
    };

    return AuthService.getInstance().CallBackendFunctionPost(
      '/api/SurveyTransferMapping/CreateSurveyTransferMapping',
      JSON.stringify(payload)
    );
  }

  // The selectable source questions for a mapping (to populate the edit dropdown).
  static GetSourceQuestions(surveyTransferMappingId) {
    return AuthService.getInstance().CallBackendFunction(
      `/api/SurveyTransferMapping/GetSourceQuestions?surveyTransferMappingId=${surveyTransferMappingId}`
    );
  }

  // Edits a single question rule: choose the source kind (SourceType), re-point its source
  // question (null allowed) or pick a job-field SourceKey.
  static UpdateQuestionMappingRule(surveyTransferQuestionMappingId, sourceTpQuestionId, sourceType, sourceKey) {
    const payload = {
      SurveyTransferQuestionMappingId: surveyTransferQuestionMappingId,
      SourceTpQuestionId:
        sourceTpQuestionId === '' || sourceTpQuestionId === null || sourceTpQuestionId === undefined
          ? null
          : parseInt(sourceTpQuestionId, 10),
      SourceType: sourceType,
      SourceKey: sourceKey === '' || sourceKey === undefined ? null : sourceKey,
    };

    return AuthService.getInstance().CallBackendFunctionPost(
      '/api/SurveyTransferMapping/UpdateSurveyTransferQuestionMapping',
      JSON.stringify(payload)
    );
  }

  // The selectable source answer options for a question mapping (to populate the edit dropdown).
  static GetSourceAnswerOptions(surveyTransferQuestionMappingId) {
    return AuthService.getInstance().CallBackendFunction(
      `/api/SurveyTransferMapping/GetSourceAnswerOptions?surveyTransferQuestionMappingId=${surveyTransferQuestionMappingId}`
    );
  }

  // Edits a single answer-option rule: re-point its source answer option (null allowed).
  static UpdateAnswerOptionMappingRule(surveyTransferAnswerOptionMappingId, sourceTpAnswerOptionId) {
    const payload = {
      SurveyTransferAnswerOptionMappingId: surveyTransferAnswerOptionMappingId,
      SourceTpAnswerOptionId:
        sourceTpAnswerOptionId === '' || sourceTpAnswerOptionId === null || sourceTpAnswerOptionId === undefined
          ? null
          : parseInt(sourceTpAnswerOptionId, 10),
    };

    return AuthService.getInstance().CallBackendFunctionPost(
      '/api/SurveyTransferMapping/UpdateSurveyTransferAnswerOptionMapping',
      JSON.stringify(payload)
    );
  }

  // Resets an existing mapping: re-runs the data import and regenerates its question/answer-option
  // rules (deleting the existing ones). Forwards to OperationResetSurveyTransferMappingData.
  static ResetSurveyTransferMappingData(item) {
    const payload = {
      SourceSystemTpInstanceId: item.SourceSystemTpInstanceId,
      SourceTpSurveyId: item.SourceTpSurveyId,
      TargetSystemTpInstanceId: item.TargetSystemTpInstanceId,
      TargetTpSurveyId: item.TargetTpSurveyId,
    };

    return AuthService.getInstance().CallBackendFunctionPost(
      '/api/SurveyTransferMapping/ResetSurveyTransferMappingData',
      JSON.stringify(payload)
    );
  }

  // Refreshes an existing mapping: reloads (upserts) the source and target survey questions and
  // answer options without deleting them and without changing the mapping rules.
  // Forwards to OperationRefreshSurveyTransferMappingData.
  static RefreshSurveyTransferMappingData(item) {
    const payload = {
      SourceSystemTpInstanceId: item.SourceSystemTpInstanceId,
      SourceTpSurveyId: item.SourceTpSurveyId,
      TargetSystemTpInstanceId: item.TargetSystemTpInstanceId,
      TargetTpSurveyId: item.TargetTpSurveyId,
    };

    return AuthService.getInstance().CallBackendFunctionPost(
      '/api/SurveyTransferMapping/RefreshSurveyTransferMappingData',
      JSON.stringify(payload)
    );
  }

  // Approves an existing mapping (stamps ApprovedOnUtc / ApprovedBy).
  // Forwards to OperationApproveSurveyTransferMappingData.
  static ApproveSurveyTransferMappingData(item) {
    const payload = {
      SourceSystemTpInstanceId: item.SourceSystemTpInstanceId,
      SourceTpSurveyId: item.SourceTpSurveyId,
      TargetSystemTpInstanceId: item.TargetSystemTpInstanceId,
      TargetTpSurveyId: item.TargetTpSurveyId,
    };

    return AuthService.getInstance().CallBackendFunctionPost(
      '/api/SurveyTransferMapping/ApproveSurveyTransferMappingData',
      JSON.stringify(payload)
    );
  }

  // Toggles the IsActive state of an existing mapping.
  // Forwards to OperationSwitchActiveStateSurveyTransferMappingData.
  static SwitchActiveStateSurveyTransferMappingData(item) {
    const payload = {
      SourceSystemTpInstanceId: item.SourceSystemTpInstanceId,
      SourceTpSurveyId: item.SourceTpSurveyId,
      TargetSystemTpInstanceId: item.TargetSystemTpInstanceId,
      TargetTpSurveyId: item.TargetTpSurveyId,
    };

    return AuthService.getInstance().CallBackendFunctionPost(
      '/api/SurveyTransferMapping/SwitchActiveStateSurveyTransferMappingData',
      JSON.stringify(payload)
    );
  }

  // Saves the mapping edit panel: the TargetStatus and the SendTriggers opt-in.
  // Forwards to OperationUpdateSurveyTransferMappingTargetStatus.
  static UpdateSurveyTransferMappingTargetStatus(item) {
    const payload = {
      SurveyTransferMappingId: item.SurveyTransferMappingId,
      TargetStatus:
        item.TargetStatus === '' || item.TargetStatus === null || item.TargetStatus === undefined
          ? null
          : parseInt(item.TargetStatus, 10),
      // The OnOff input can hand back a string ('true'/'false'); coerce so the backend bool? binds.
      SendTriggers: item.SendTriggers === true || item.SendTriggers === 'true',
    };

    return AuthService.getInstance().CallBackendFunctionPost(
      '/api/SurveyTransferMapping/UpdateSurveyTransferMappingTargetStatus',
      JSON.stringify(payload)
    );
  }
}

export default SurveyTransferMappingService;
